/** Parse agent variables string (e.g. "hasupload :false" or JSON). */
export const parseAgentVariables = (variables) => {
  if (!variables) return {};
  if (typeof variables === "object" && !Array.isArray(variables)) {
    return variables;
  }
  if (typeof variables !== "string") return {};

  const trimmed = variables.trim();
  if (!trimmed) return {};

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch {
    // fall through to loose key:value parsing
  }

  const result = {};
  trimmed.split(/[,;]/).forEach((pair) => {
    const match = pair.trim().match(/^([^:]+)\s*:\s*(.+)$/);
    if (!match) return;
    const key = match[1].trim().replace(/\s+/g, "").toLowerCase();
    const rawValue = match[2].trim();
    const lowerValue = rawValue.toLowerCase();
    if (lowerValue === "true") result[key] = true;
    else if (lowerValue === "false") result[key] = false;
    else result[key] = rawValue;
  });
  return result;
};

/** UI-only keys on agent.variables — not shown or sent on manual run. */
const AGENT_UI_CONFIG_VARIABLE_KEYS = new Set(["hasupload", "has_upload"]);

export const normalizeAgentVariableKey = (key) =>
  String(key).trim().toLowerCase().replace(/\s+/g, "").replace(/-/g, "_");

export const isAgentUiConfigVariable = (key) =>
  AGENT_UI_CONFIG_VARIABLE_KEYS.has(normalizeAgentVariableKey(key));

export const omitAgentUiConfigVariables = (record) => {
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(record).filter(([key]) => !isAgentUiConfigVariable(key))
  );
};

/** Show Upload Invoice / document viewer unless variables explicitly set hasupload to false. */
export const agentHasUploadEnabled = (agent) => {
  const variables = agent?.fullData?.variables ?? agent?.variables;
  const parsed = parseAgentVariables(variables);
  const hasUpload = parsed.hasupload ?? parsed.has_upload;
  if (hasUpload === false || String(hasUpload).toLowerCase() === "false") {
    return false;
  }
  return true;
};

export const fetchAgentById = async (axiosInstance, agentId) => {
  if (!agentId) return null;

  try {
    const response = await axiosInstance.get(`/api/v1/diy_agents/${agentId}`);
    return response.data?.data ?? response.data ?? null;
  } catch {
    try {
      const response = await axiosInstance.get("/api/v1/diy_agents", {
        params: { page: 1, size: 100, is_publish: true },
      });
      const agents = response.data?.data ?? [];
      return agents.find((agent) => agent.id === agentId) ?? null;
    } catch {
      return null;
    }
  }
};

export const shouldHideDocumentViewerForAgent = (agent) =>
  Boolean(agent && !agentHasUploadEnabled(agent));
